import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { ExportButton, useToast, Button, SortableDataTable } from "@perfectserve/library";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { groupInteractionsByAccount, groupInteractionsByUser, filterAccountGroups, filterUserGroups, shouldShowAggregateCard, } from "../utils/executiveSummaryUtils";
import { calculateAccountMetrics, calculateUserMetrics } from "../utils/accountSummaryCalculations";
import { useExecutiveSummaryColumns } from "./ExecutiveSummary/ExecutiveSummaryColumns";
import { handleExecutiveSummaryExcelExport } from "../utils/executiveSummaryExportUtils";
const ExecutiveSummary = ({ interactions, kpiResult, selectedAccountNames = [], selectedUserNames = [], viewSettings, summaryMetrics, onClose, }) => {
    const { showToast } = useToast();
    const accountGroups = useMemo(() => groupInteractionsByAccount(interactions), [interactions]);
    const userGroups = useMemo(() => groupInteractionsByUser(interactions), [interactions]);
    const filteredAccountGroups = useMemo(() => filterAccountGroups(accountGroups, selectedAccountNames), [accountGroups, selectedAccountNames]);
    const filteredUserGroups = useMemo(() => filterUserGroups(userGroups, selectedUserNames), [userGroups, selectedUserNames]);
    const accountRows = useMemo(() => calculateAccountMetrics(filteredAccountGroups, kpiResult), [filteredAccountGroups, kpiResult]);
    const userRows = useMemo(() => calculateUserMetrics(filteredUserGroups, selectedUserNames, kpiResult), [filteredUserGroups, selectedUserNames, kpiResult]);
    const getAggregateCardName = (userNames, accountNames) => {
        if (userNames.length > 0 && accountNames.length > 0) {
            return "All Selected Accounts & Users";
        }
        if (userNames.length > 0) {
            return "All Selected Users";
        }
        return "All Selected Accounts";
    };
    const accountSummaryData = useMemo(() => {
        if (!interactions.length)
            return [];
        const result = [];
        if (shouldShowAggregateCard(selectedAccountNames, selectedUserNames) && summaryMetrics) {
            const aggregateRow = {
                accountName: getAggregateCardName(selectedUserNames, selectedAccountNames),
                practices: summaryMetrics.practices,
                totalInteractions: summaryMetrics.totalInteractions,
                urgent: summaryMetrics.urgent,
                routine: summaryMetrics.routine,
                avgUrgentReadTime: summaryMetrics.avgUrgentReadTime,
                medianUrgentReadTime: summaryMetrics.medianUrgentReadTime,
                avgCallbackTime: summaryMetrics.avgCallbackTime,
                medianCallbackTime: summaryMetrics.medianCallbackTime,
                avgCallbackDuration: summaryMetrics.avgCallbackDuration,
                medianCallbackDuration: summaryMetrics.medianCallbackDuration,
                avgUrgentReadTimeSeconds: summaryMetrics.avgUrgentReadTimeSeconds,
                medianUrgentReadTimeSeconds: summaryMetrics.medianUrgentReadTimeSeconds,
                avgCallbackTimeSeconds: summaryMetrics.avgCallbackTimeSeconds,
                medianCallbackTimeSeconds: summaryMetrics.medianCallbackTimeSeconds,
                avgCallbackDurationSeconds: summaryMetrics.avgCallbackDurationSeconds,
                medianCallbackDurationSeconds: summaryMetrics.medianCallbackDurationSeconds,
                callbackCount: summaryMetrics.callbackCount,
                voicemails: summaryMetrics.voicemails,
                transfers: summaryMetrics.transfers,
                isBackup: summaryMetrics.isBackup,
                isForward: summaryMetrics.isForward,
                kpiExcellent: kpiResult === null || kpiResult === void 0 ? void 0 : kpiResult.excellentCount,
                kpiGood: kpiResult === null || kpiResult === void 0 ? void 0 : kpiResult.goodCount,
                kpiAcceptable: kpiResult === null || kpiResult === void 0 ? void 0 : kpiResult.acceptableCount,
                kpiPoor: kpiResult === null || kpiResult === void 0 ? void 0 : kpiResult.poorCount,
                kpiExcellentPercent: kpiResult ? `${Math.round(kpiResult.excellentPct)}%` : undefined,
                kpiGoodPercent: kpiResult ? `${Math.round(kpiResult.goodPct)}%` : undefined,
                kpiAcceptablePercent: kpiResult ? `${Math.round(kpiResult.acceptablePct)}%` : undefined,
                kpiPoorPercent: kpiResult ? `${Math.round(kpiResult.poorPct)}%` : undefined,
                isTotal: false,
            };
            result.push(aggregateRow);
        }
        const sortedAccountRows = accountRows.sort((a, b) => a.accountName.localeCompare(b.accountName));
        result.push(...sortedAccountRows);
        const sortedUserRows = userRows.sort((a, b) => a.accountName.localeCompare(b.accountName));
        result.push(...sortedUserRows);
        return result;
    }, [interactions, accountRows, userRows, selectedAccountNames, selectedUserNames, kpiResult, summaryMetrics]);
    const columns = useExecutiveSummaryColumns({ viewSettings, kpiResult });
    const handleExcelExport = async () => {
        try {
            await handleExecutiveSummaryExcelExport(accountSummaryData, viewSettings, kpiResult);
        }
        catch (error) {
            console.error("Excel export failed:", error);
            showToast({
                message: "Excel export failed. Please try again.",
                type: "error",
                position: "top-right",
            });
        }
    };
    return (_jsxs("div", { className: "interactions-container", style: { minHeight: "100vh" }, children: [_jsx("div", { className: "interactions-header", children: _jsxs("div", { className: "interactions-controls d-flex justify-content-between align-items-center w-100", children: [onClose && (_jsx(Button, { onClick: onClose, icon: faArrowLeft, iconPosition: "left", children: "Back to Dashboard" })), _jsx("div", { className: "bold text-center flex-1", children: "Executive Summary" }), _jsx(ExportButton, { title: "Executive_Summary", onExcelExport: handleExcelExport, exportElementId: "executive-summary-content", variant: "links", buttonStyle: { fontSize: "12px" }, hidePdfExport: true })] }) }), _jsx("div", { className: "bg-white border border-gray-300 rounded-8 shadow-sm mx-20 mb-20", style: { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }, children: _jsx("div", { id: "executive-summary-content", children: _jsx(SortableDataTable, { columns: columns, data: accountSummaryData, defaultSortKey: "accountName", defaultSortDirection: "asc", getRowId: (row, index) => `${row.accountName}-${index}`, emptyMessage: "No executive summary data available" }) }) })] }));
};
export default ExecutiveSummary;
//# sourceMappingURL=ExecutiveSummary.js.map